import { computed, ref, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import {
  Home,
  BarChart3,
  Landmark,
  Wallet,
  Layers,
  Users,
  ReceiptText,
  Network,
  Briefcase,
  Settings,
  LogOut,
  KeyRound,
  UserCog,
} from 'lucide-vue-next'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { rails_url } from '@/modules/shared/utils/rails'

export type NavSubItem = {
  key: string
  label: string
  to: string | Record<string, unknown>
  hidden?: boolean
  // Route-name prefixes that also mark this sub-item active (e.g. the section's
  // detail/new/edit routes), so the group stays expanded while inside the section.
  activePrefixes?: string[]
  // For sub-items that share a route with a sibling (Investors & Admins both use the
  // individual-detail route): on that route, active only when the current Accounts
  // view source matches this value.
  viewSourceMatch?: 'admins' | 'individuals'
}

export type NavItem = {
  key: string
  label: string
  icon: Component
  to?: string | Record<string, unknown>
  hidden?: boolean
  badge?: string
  subItems?: NavSubItem[]
  // When true, sub-items are hidden until the group is active (collapsible).
  // When omitted, sub-items are always visible (e.g. Settings).
  collapsible?: boolean
}

export type ProfileLink = {
  label: string
  to: string | Record<string, unknown>
  icon: Component
}

export type Firm = {
  name: string
  subdomain: string
}
// Investors and Admins share the individual-detail route (investing.individual-*),
// so the route alone can't say which section the user is browsing. This module-level
// ref records the last Accounts list they visited; both the side-nav highlight and the
// individual-header back-link read it. Defaults to 'individuals'.
const accountsViewSource = ref<'admins' | 'individuals'>('individuals')
export function useAccountsViewSource() {
  return accountsViewSource
}

function titleCase(str: string | null | undefined) {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.substring(1))
    .join(' ')
}

/**
 * Shared sidenav data — nav items, profile links, workspace selector — used by
 * `the-sidenav.vue` for both the desktop rail and the mobile drawer.
 */
export function useSidenav() {
  const route = useRoute()
  const authStore = useAuthStore()
  const workspaceStore = useWorkspaceStore()

  const taxYear = new Date().getFullYear() - 1

  // Company users must be checked before isAdmin — its `role !== 'investor'`
  // heuristic would otherwise put them in the admin branches.
  const isCompanyUser = computed(() => authStore.is_company_user)
  const isAdmin = computed(() => authStore.current_user.role !== 'investor')
  const current_user = computed(() => authStore.current_user)

  // Investors only see the Companies list with an active / past-due subscription.
  const canViewCompanies = computed(() => ['active', 'past-due'].includes(authStore.current_user.status))

  /////////////////////////////////////////////////////////////////////////////
  // Firm switcher
  /////////////////////////////////////////////////////////////////////////////

  // The firms (groups) the user can switch between, sorted alphabetically by name.
  const firms = computed<Firm[]>(() =>
    (authStore.current_user?.groups ?? [])
      .map((group: any) => ({ name: group.name, subdomain: group.subdomain }))
      .sort((a: Firm, b: Firm) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
  )

  const isFirmActive = (firm: Firm) => firm.subdomain === route.params.slug

  // Switching firm reloads into the selected firm's workspace.
  function selectFirm(firm: Firm) {
    if (isFirmActive(firm)) return
    const baseUrl = import.meta.env.VITE_VENTURE360_REDIRECT_URL
    window.location.href = `${baseUrl}/user?slug=${firm.subdomain}`
  }

  /////////////////////////////////////////////////////////////////////////////
  // Profile links
  /////////////////////////////////////////////////////////////////////////////

  const profile_links = computed<ProfileLink[]>(() => {
    // Company users have no investor record, so the investor-scoped account
    // and login-settings routes don't apply (tickets/2658).
    if (isCompanyUser.value) {
      return [{ label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut }]
    }
    if (isAdmin.value) {
      return [
        {
          label: 'Login Settings',
          to: {
            name: 'investing.individual.login-information',
            params: { individual_id: current_user.value.investor_id },
          },
          icon: KeyRound,
        },
        { label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut },
      ]
    }
    if (route.params.slug === 'connections') {
      return [{ label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut }]
    }
    return [
      {
        label: 'My Account',
        to: { name: 'investing.individual.account', params: { individual_id: current_user.value.investor_id } },
        icon: UserCog,
      },
      {
        label: 'Login Settings',
        to: {
          name: 'investing.individual.login-information',
          params: { individual_id: current_user.value.investor_id },
        },
        icon: KeyRound,
      },
      { label: 'Log Out', to: `${rails_url()}/logout`, icon: LogOut },
    ]
  })

  /////////////////////////////////////////////////////////////////////////////
  // Navigation items
  /////////////////////////////////////////////////////////////////////////////

  const navItems = computed<(NavItem | null)[]>(() => {
    // Company users get a single Company link (tickets/2658): the profile is
    // their dashboard, and its tab strip already covers Profile/Applications/
    // Files/Discussions. Legacy Accounts (My Investors) is not carried over —
    // community investors are deprecated.
    if (isCompanyUser.value) {
      const slug = route.params.slug as string
      const companyId = String(current_user.value.company_id ?? '')
      const companyParams = { slug, companyId }
      return [
        {
          key: 'company',
          label: 'Company',
          icon: Landmark,
          to: { name: 'companies.profile', params: companyParams },
          activePrefixes: ['companies.'],
        },
      ]
    }

    if (isAdmin.value) {
      return [
        { key: 'dashboard', label: 'Dashboard', icon: Home, to: { name: 'dashboard' } },
        { key: 'portfolio', label: 'Portfolio', icon: BarChart3, to: { name: 'investing.portfolio' } },
        {
          key: 'companies',
          label: 'Companies',
          icon: Landmark,
          to: { name: 'companies.list', params: { slug: route.params.slug } },
          collapsible: true,
          subItems: [
            {
              key: 'companies-list',
              label: 'All Companies',
              to: { name: 'companies.list', params: { slug: route.params.slug } },
              // keeps the group expanded across company detail routes
              activePrefixes: ['companies.'],
            },
            {
              // Retired into the unified pipeline board (tickets/3022) —
              // applications review lives on the board's first column.
              key: 'companies-deal-pipeline',
              label: 'Deal Pipeline',
              to: { name: 'deals.pipeline' },
              hidden: !(authStore.is_site_admin || authStore.is_group_admin),
            },
            {
              // Application builder (tickets/2617) — replaces the legacy Rails
              // "Entrepreneur Applications" entry, nested under Companies like
              // the backend navigation hierarchy.
              key: 'companies-entrepreneur-applications',
              label: 'Entrepreneur Applications',
              to: { name: 'applications.list', params: { slug: route.params.slug } },
              activePrefixes: ['applications.'],
              hidden: !(authStore.is_site_admin || authStore.is_group_admin),
            },
            {
              key: 'companies-tags',
              label: 'Tags',
              to: { name: 'tagging.tags' },
              hidden: !(authStore.is_site_admin || authStore.is_group_admin),
            },
          ],
        },
        null,
        {
          key: 'funds',
          label: 'Funds',
          icon: Wallet,
          to: { name: 'investing.entities', params: { entity_type: 'funds' } },
        },
        {
          key: 'spvs',
          label: 'SPVs',
          icon: Layers,
          to: { name: 'investing.entities', params: { entity_type: 'spvs' } },
        },
        {
          key: 'accounts',
          label: 'Accounts',
          icon: Users,
          to: { name: 'investing.individuals' },
          collapsible: true,
          subItems: [
            // Investors & Admins share the individual-detail route, so they're
            // disambiguated by view source (see viewSourceMatch) rather than the route.
            {
              key: 'accounts-investors',
              label: 'Individuals',
              to: { name: 'investing.individuals' },
              viewSourceMatch: 'individuals',
            },
            {
              key: 'accounts-associated-entities',
              label: 'Associated Entities',
              to: { name: 'investing.associated-entities' },
              activePrefixes: ['investing.associated-entit'],
            },
            {
              key: 'accounts-sub-accounts',
              label: 'Sub Accounts',
              to: { name: 'investing.subgroups' },
              activePrefixes: ['investing.subgroup'],
            },
            { key: 'accounts-admins', label: 'Admins', to: { name: 'investing.admins' }, viewSourceMatch: 'admins' },
          ],
        },
        null,
        {
          key: 'taxes',
          label: 'Taxes',
          icon: ReceiptText,
          to: {
            name: 'tax.entities',
            params: { slug: route.params.slug },
            query: { year: taxYear },
          },
          hidden: !workspaceStore.current.has_entities_with_taxes,
        },
        {
          key: 'connections',
          label: 'Connections',
          icon: Network,
          to: { name: 'connecting.liquifi' },
          hidden: !workspaceStore.current.options.has_connections_access,
        },
        {
          key: 'management',
          label: 'Management',
          icon: Briefcase,
          to: { name: 'investing.documents' },
          collapsible: true,
          subItems: [
            { key: 'management-documents', label: 'Documents', to: { name: 'investing.documents' } },
            { key: 'management-invoices', label: 'Invoices', to: { name: 'investing.invoices' } },
            {
              key: 'management-message-center',
              label: 'Message Center',
              to: { name: 'messaging.index' },
              activePrefixes: ['messaging.'],
            },
            { key: 'management-files', label: 'Files', to: { name: 'filing.group.files-system' } },
            {
              key: 'management-billing',
              label: 'Billing',
              to: { name: 'billing-group' },
              activePrefixes: ['billing-group'],
            },
          ],
        },
        {
          key: 'settings',
          label: 'Settings',
          icon: Settings,
          to: { name: 'workspace.settings' },
          subItems: [
            { key: 'settings-general', label: 'General', to: { name: 'workspace.settings' } },
            { key: 'settings-investor', label: 'Investor Settings', to: { name: 'workspace.settings.investor' } },
            { key: 'settings-company', label: 'Company Settings', to: { name: 'workspace.settings.company' } },
            { key: 'settings-reminders', label: 'Automated Reminders', to: { name: 'workspace.settings.reminders' } },
            { key: 'settings-emails', label: 'Email Notifications', to: { name: 'workspace.settings.emails' } },
          ],
        },
      ]
    }

    if (route.params.slug === 'connections') {
      return [
        {
          key: 'connections',
          label: 'Connections',
          icon: Network,
          to: { name: 'connecting.liquifi', params: { slug: route.params.slug } },
        },
      ]
    }

    // The investor's Companies group (tickets/3128): mirrors the admin
    // structure — All Companies plus the investor's Deal Pipeline when the
    // group setting admits them. All Companies rides the same gates the
    // flat link did (company-index setting + active subscription, backed by
    // CompanyPolicy#index?); the pipeline link mirrors DealPolicy#index?.
    // The group header lands on whichever sub-item is available, so it can
    // never deep-link a surface the API would 403.
    const companiesListVisible = workspaceStore.current.options.is_company_list_enabled && canViewCompanies.value
    const pipelineVisible =
      workspaceStore.current.options.is_investment_group && workspaceStore.current.options.deal_pipeline_enabled

    return [
      { key: 'home', label: 'Home', icon: Home, to: { name: 'dashboard' } },
      {
        key: 'accounts',
        label: 'Accounts',
        icon: Users,
        to: { name: 'investing.individuals' },
        hidden: workspaceStore.current.individual_count === 0,
      },
      {
        key: 'companies',
        label: 'Companies',
        icon: Landmark,
        to: companiesListVisible
          ? { name: 'companies.list', params: { slug: route.params.slug } }
          : { name: 'deals.pipeline' },
        collapsible: true,
        hidden: !(companiesListVisible || pipelineVisible),
        subItems: [
          {
            key: 'companies-list',
            label: 'All Companies',
            to: { name: 'companies.list', params: { slug: route.params.slug } },
            // keeps the group expanded across company detail routes
            activePrefixes: ['companies.'],
            hidden: !companiesListVisible,
          },
          {
            key: 'companies-deal-pipeline',
            label: 'Deal Pipeline',
            to: { name: 'deals.pipeline' },
            hidden: !pipelineVisible,
          },
        ],
      },
      {
        key: 'connections',
        label: 'Connections',
        icon: Network,
        to: { name: 'connecting.liquifi', params: { slug: route.params.slug } },
        hidden: !workspaceStore.current.options.has_connections_access,
      },
    ]
  })

  const isNavActive = (nav: NavItem) => {
    if (!nav.to || typeof nav.to === 'string') return false
    // Entity detail routes carry the singular entity_type ('spv'), while the
    // Funds/SPVs list links use the plural ('spvs') — normalize both so the
    // section stays highlighted anywhere inside an entity.
    if (nav.to.name === 'investing.entities') {
      const current = String(route.params.entity_type ?? '').replace(/s$/, '')
      const target = String((nav.to as any).params.entity_type ?? '').replace(/s$/, '')
      return current === target
    }
    return route.name === nav.to.name
  }

  // Investors & Admins both resolve to this route, so the route name alone can't say
  // which Accounts section the user is in.
  const isIndividualDetailRoute = (name: string) => /^investing\.individual[-.]/.test(name)

  const isSubItemActive = (subItem: NavSubItem) => {
    const name = String(route.name ?? '')

    // On the shared individual-detail route, disambiguate by where the user came from.
    if (subItem.viewSourceMatch && isIndividualDetailRoute(name)) {
      return accountsViewSource.value === subItem.viewSourceMatch
    }

    if (subItem.activePrefixes?.length && subItem.activePrefixes.some((prefix) => name.startsWith(prefix))) return true
    if (typeof subItem.to === 'string') return false
    return route.name === subItem.to.name
  }

  const isGroupActive = (item: NavItem) => !!item.subItems?.some((subItem) => isSubItemActive(subItem))

  // Record which Accounts list the user is in. Visiting a list sets the source;
  // individual-detail routes keep the current value (so it persists across the
  // profile's own tabs); leaving the Accounts area resets to the default.
  watch(
    () => String(route.name ?? ''),
    (name) => {
      if (name === 'investing.admins') accountsViewSource.value = 'admins'
      else if (name === 'investing.individuals') accountsViewSource.value = 'individuals'
      else if (!isIndividualDetailRoute(name)) accountsViewSource.value = 'individuals'
    },
    { immediate: true },
  )

  return {
    titleCase,
    isAdmin,
    current_user,
    firms,
    isFirmActive,
    selectFirm,
    profile_links,
    navItems,
    isNavActive,
    isSubItemActive,
    isGroupActive,
  }
}
